import React, { useContext } from 'react';
import isEqual from 'react-fast-compare';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-types-papi';
import OnScrollBottom, {
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import withSectionFetchSpinner from '../../../../decorators/withSectionFetchSpinner';
import { PostsSortOrder } from '../../../../../types/posts';
import { USER_PROFILE_POSTS_SUB_PATHS } from '@wix/da-url';
import WidgetHeader from '../../../UserWidgets/_partials/WidgetHeader';
import EmptyState from '../_partials/EmptyState';
import PostsMobileList from '../_partials/PostsMobileList';
import JournalsGrid from '../_partials/PostsGrid';
import SortOrderSwitcher from '../_partials/SortOrderSwitcher';

import JournalsList from './JournalsList';
import FeaturedJournals from './FeaturedList';

import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SectionViewType } from '../../../../../types/sections';

import s from './Journals.scss';

export interface Props {
  streamId: string;
  featuredStreamId: string;
  viewType: SectionViewType;
  journals: PapiDeviation[];
  featuredJournals: PapiDeviation[];
  hasMoreJournals: boolean;
  isFetchingJournals: boolean;
  fetchMoreJournals: () => void;
  sortOrder: PostsSortOrder;
  onSortOrderChange: (
    path: USER_PROFILE_POSTS_SUB_PATHS,
    sortOrder: PostsSortOrder
  ) => void;
  children?: React.ReactNode;
}

const Journals: React.FC<Props> = ({
  streamId,
  featuredStreamId,
  viewType,
  journals,
  featuredJournals,
  hasMoreJournals,
  isFetchingJournals,
  fetchMoreJournals,
  sortOrder,
  onSortOrderChange,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const updateSortOrder = (sort: PostsSortOrder) => {
    onSortOrderChange &&
      onSortOrderChange(USER_PROFILE_POSTS_SUB_PATHS.JOURNALS, sort);
  };

  const JournalsComponent =
    viewType === SectionViewType.LIST ? JournalsList : JournalsGrid;

  if (!journals.length && !featuredJournals.length) {
    return (
      <EmptyState
        className={s['empty-state']}
        postType="journal"
        view="large"
        CTAButtonModalType={ModalType.JOURNAL}
      />
    );
  }

  return (
    <div className={s['root']}>
      <ErrorBoundary>
        <div
          className={classnames(
            s['content'],
            journals.length === 0 && s['empty']
          )}
        >
          <OnScrollBottom
            distanceToBottom={2}
            distanceUnit={DISTANCE_UNIT.WINDOW_HEIGHT}
            onScrolledToBottom={
              hasMoreJournals && !isFetchingJournals
                ? fetchMoreJournals
                : undefined
            }
          >
            <div className={s['list']}>
              {isMobile ? (
                journals.length === 0 ? (
                  <EmptyState
                    postType="journal"
                    view="large"
                    CTAButtonModalType={ModalType.JOURNAL}
                  />
                ) : (
                  <PostsMobileList streamId={streamId} items={journals} />
                )
              ) : (
                <>
                  <WidgetHeader
                    extras={
                      <SortOrderSwitcher
                        className={s['sort-order-switch']}
                        value={sortOrder}
                        onChange={updateSortOrder}
                      />
                    }
                  />
                  <JournalsComponent streamId={streamId} items={journals} />
                </>
              )}
              {isFetchingJournals && <LoadingIndicator />}
            </div>
          </OnScrollBottom>
        </div>
        {!isMobile && viewType === SectionViewType.LIST && (
          <div
            className={classnames(
              s['sidebar'],
              featuredJournals.length === 0 && s['empty']
            )}
          >
            <WidgetHeader
              header={t('common.featuredJournals')}
              className={s['sidebar-header']}
            />
            <div className={s['list']}>
              <FeaturedJournals
                streamId={featuredStreamId}
                items={featuredJournals}
              />
            </div>
          </div>
        )}
      </ErrorBoundary>
    </div>
  );
};
Journals.displayName = 'Journals';

export default React.memo(withSectionFetchSpinner(Journals), isEqual);
